import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum';

import { EventTrackingAction, UserEvent } from '../declarations';
import { DeployEnv } from './deployEnv';
import { ContextualData, emptyContextualData } from '../contextualData';

export type DatadogOptions = Omit<Omit<RumInitConfiguration, 'env'>, 'version'> & {
  service: string /* required for tracing */;
};

export function initDatadog(env: DeployEnv, gitSha: string, options: DatadogOptions): void {
  datadogRum.init({
    site: 'datadoghq.com',
    version: gitSha,
    env,
    ...options,
  });
  datadogRum.startSessionReplayRecording();
}

export function getContextualDataFromDatadog(): ContextualData {
  const sessionId = datadogRum.getInternalContext()?.session_id;
  if (sessionId) {
    return {
      data: {
        'datadog-rum-session-id': sessionId,
        'datadog-rum-session-replay': `https://app.datadoghq.com/rum/replay/sessions/${sessionId}`,
        'datadog-rum-session-search': `https://app.datadoghq.com/rum/explorer?query=%40session.id%3A${sessionId}`,
      },
    };
  } else {
    return emptyContextualData;
  }
}

export function trackPerformanceInDatadog(eventName: string) {
  datadogRum.addTiming(eventName);
}

export function addRUMGlobalContext(context: { [k: string]: unknown }) {
  datadogRum.setRumGlobalContext({ ...datadogRum.getRumGlobalContext(), ...context });
}

export function trackEventInDatadog(
  action: EventTrackingAction,
  label?: string,
  contextualData: ContextualData = emptyContextualData,
) {
  datadogRum.addAction(action, {
    ...contextualData.data,
    adClickContext: contextualData.adClickContext,
    campaignContext: contextualData.campaignContext,
    customerContext: contextualData.customerContext,
    leadContext: contextualData.leadContext,
    sellDirectContext: contextualData.sellDirectContext,
    label,
  });
}

export function trackUserEventInDatadog(
  event: UserEvent,
  contextualData: ContextualData = emptyContextualData,
) {
  datadogRum.addAction(Object.keys(event)[0] ?? 'unknownUserEvent', {
    ...event,
    ...contextualData.data,
    adClickContext: contextualData.adClickContext,
    campaignContext: contextualData.campaignContext,
    customerContext: contextualData.customerContext,
    leadContext: contextualData.leadContext,
    sellDirectContext: contextualData.sellDirectContext,
  });
}

export function trackErrorInDatadog(
  label: string,
  error?: Error,
  contextualData: ContextualData = emptyContextualData,
) {
  if (shouldExcludeErrorByMessage(error)) {
    return;
  }

  datadogRum.addError(error || new Error(label), {
    ...contextualData.data,
    label,
  });
}

// Array will perform better than Set when N is tiny by avoiding a hashing operation. Consider converting to a
// Set<string> if N ever becomes large, and after benchmarking performance.
const ERROR_MESSAGES_TO_EXCLUDE: string[] = [
  // Error that MAY indicate a performance issue due to browser implementation, but has been shown to be safe to ignore.
  // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
  'ResizeObserver loop limit exceeded',
];

export const shouldExcludeErrorByMessage = (error?: Error): boolean => {
  return ERROR_MESSAGES_TO_EXCLUDE.includes(error?.message || '');
};
